import { useEffect, useState } from 'react'
import { ingestionApiService, type IngestionSource } from '../services/ingestionApiService'
import { ApiError } from '../services/BaseApiService'
import { colors, fontFamily, fontSize, radius, shadow, spacing } from '../styles/theme'
import { Select, useDialogs } from '../components/ui'

const SOURCE_TYPES = [
  { value: 'postgres', label: 'PostgreSQL' },
  { value: 'mysql',    label: 'MySQL' },
  { value: 'mongodb',  label: 'MongoDB' },
  { value: 'rest',     label: 'REST API' },
]

const CREDENTIAL_TEMPLATES: Record<string, Record<string, unknown>> = {
  postgres: { connectionString: 'Host=host;Port=5432;Database=db;Username=user;Password=pass' },
  mysql:    { connectionString: 'Server=host;Port=3306;Database=db;Uid=user;Pwd=pass;' },
  mongodb:  { connectionString: 'mongodb://user:pass@host:27017', database: 'db', collection: 'col' },
  rest:     { authorization: 'Bearer <token>' },
}

const cardStyle: React.CSSProperties = {
  background: colors.surface,
  border: `1px solid ${colors.border}`,
  borderRadius: radius.md,
  padding: spacing.lg,
  boxShadow: shadow.sm,
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: `${spacing.sm} ${spacing.md}`,
  border: `1px solid ${colors.border}`,
  borderRadius: radius.sm,
  background: colors.background,
  color: colors.text,
  fontSize: fontSize.base,
  boxSizing: 'border-box',
}

const btnStyle: React.CSSProperties = {
  padding: `${spacing.sm} ${spacing.lg}`,
  borderRadius: radius.sm,
  border: 'none',
  cursor: 'pointer',
  fontSize: fontSize.sm,
  fontWeight: 600,
}

export default function IngestionSourcesPage() {
  const dialogs = useDialogs()
  const [sources, setSources]       = useState<IngestionSource[]>([])
  const [loading, setLoading]       = useState(true)
  const [error, setError]           = useState('')
  const [showCreate, setShowCreate] = useState(false)
  const [saving, setSaving]         = useState(false)

  const [name, setName]             = useState('')
  const [sourceType, setSourceType] = useState('postgres')
  const [credsJson, setCredsJson]   = useState(
    JSON.stringify(CREDENTIAL_TEMPLATES['postgres'], null, 2)
  )
  const [credsError, setCredsError] = useState('')

  useEffect(() => { load() }, [])

  async function load() {
    try {
      setSources(await ingestionApiService.list())
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Failed to load.')
    } finally {
      setLoading(false)
    }
  }

  function handleTypeChange(t: string) {
    setSourceType(t)
    setCredsJson(JSON.stringify(CREDENTIAL_TEMPLATES[t] ?? {}, null, 2))
    setCredsError('')
  }

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault()
    setCredsError('')
    let creds: Record<string, unknown>
    try { creds = JSON.parse(credsJson) } catch {
      setCredsError('Credentials must be valid JSON.')
      return
    }
    setSaving(true)
    try {
      await ingestionApiService.create({ name, sourceType, credentials: creds })
      setShowCreate(false)
      setName('')
      setSourceType('postgres')
      setCredsJson(JSON.stringify(CREDENTIAL_TEMPLATES['postgres'], null, 2))
      await load()
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Create failed.')
    } finally {
      setSaving(false)
    }
  }

  async function handleDelete(sourceId: string) {
    if (!(await dialogs.confirm({ title: 'Delete this ingestion source?', confirmLabel: 'Delete', danger: true }))) return
    try {
      await ingestionApiService.delete(sourceId)
      setSources(s => s.filter(x => x.ingestionSourceId !== sourceId))
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Delete failed.')
    }
  }

  return (
    <>
    <div style={{ padding: spacing.xl, maxWidth: 900, margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: spacing.xl }}>
        <div>
          <h1 style={{ margin: 0, fontSize: fontSize['2xl'], fontWeight: 600, letterSpacing: '-0.02em', color: colors.text }}>Ingestion Sources</h1>
          <p style={{ margin: `${spacing.xs} 0 0`, color: colors.textMuted, fontSize: fontSize.sm }}>
            Connect to Postgres, MySQL, MongoDB, or REST APIs. Fetch data by record ID and render templates automatically.
          </p>
        </div>
        <button
          onClick={() => setShowCreate(!showCreate)}
          style={{ ...btnStyle, background: colors.primary, color: colors.onPrimary }}
        >
          {showCreate ? 'Cancel' : '+ New Source'}
        </button>
      </div>

      {error && (
        <div style={{ background: colors.errorLight, border: `1px solid ${colors.errorBorder}`, borderRadius: radius.sm, padding: spacing.md, marginBottom: spacing.lg, color: colors.error }}>
          {error}
        </div>
      )}

      {showCreate && (
        <div style={{ ...cardStyle, marginBottom: spacing.xl }}>
          <h2 style={{ margin: `0 0 ${spacing.lg}`, fontSize: fontSize.lg }}>New Ingestion Source</h2>
          <form onSubmit={handleCreate}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: spacing.md, marginBottom: spacing.md }}>
              <div>
                <label htmlFor="ingestion-name" style={{ display: 'block', fontSize: fontSize.sm, fontWeight: 600, marginBottom: spacing.xs }}>Name</label>
                <input id="ingestion-name"
                  style={inputStyle}
                  value={name}
                  onChange={e => setName(e.target.value)}
                  placeholder="My Postgres Source"
                  required
                />
              </div>
              <div>
                <label htmlFor="ingestion-source-type" style={{ display: 'block', fontSize: fontSize.sm, fontWeight: 600, marginBottom: spacing.xs }}>Source Type</label>
                <Select id="ingestion-source-type"
                  value={sourceType}
                  onChange={v => handleTypeChange(String(v))}
                  options={SOURCE_TYPES}
                />
              </div>
            </div>
            <div style={{ marginBottom: spacing.md }}>
              <label htmlFor="ingestion-credentials-json-encrypted" style={{ display: 'block', fontSize: fontSize.sm, fontWeight: 600, marginBottom: spacing.xs }}>
                Credentials (JSON) — encrypted at rest
              </label>
              <textarea id="ingestion-credentials-json-encrypted"
                style={{ ...inputStyle, height: 140, fontFamily: fontFamily.mono, resize: 'vertical' }}
                value={credsJson}
                onChange={e => setCredsJson(e.target.value)}
              />
              {credsError && <p style={{ color: colors.error, fontSize: fontSize.sm, margin: `${spacing.xs} 0 0` }}>{credsError}</p>}
              <p style={{ color: colors.textMuted, fontSize: fontSize.xs, margin: `${spacing.xs} 0 0` }}>
                {sourceType === 'postgres' || sourceType === 'mysql'
                  ? 'Use @RecordId in your query to reference the record ID passed at fetch time.'
                  : sourceType === 'mongodb'
                  ? 'Use {recordId} in the filter JSON to reference the record ID.'
                  : 'Use {recordId} in the URL template passed at fetch time.'}
              </p>
            </div>
            <div style={{ display: 'flex', gap: spacing.sm }}>
              <button
                type="submit"
                disabled={saving}
                style={{ ...btnStyle, background: colors.primary, color: colors.onPrimary, opacity: saving ? 0.6 : 1 }}
              >
                {saving ? 'Saving…' : 'Create Source'}
              </button>
            </div>
          </form>
        </div>
      )}

      {loading ? (
        <p style={{ color: colors.textMuted }}>Loading…</p>
      ) : sources.length === 0 ? (
        <div style={{ ...cardStyle, textAlign: 'center', padding: spacing.xxl }}>
          <p style={{ color: colors.textMuted, margin: 0 }}>No ingestion sources yet. Create one to get started.</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.md }}>
          {sources.map(s => (
            <div key={s.ingestionSourceId} style={cardStyle}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={{ fontWeight: 600, color: colors.text }}>{s.name}</div>
                  <div style={{ fontSize: fontSize.sm, color: colors.textMuted, marginTop: spacing.xs }}>
                    {SOURCE_TYPES.find(t => t.value === s.sourceType)?.label ?? s.sourceType}
                    {' · '}
                    <span style={{ color: s.isActive ? colors.success : colors.textMuted }}>
                      {s.isActive ? 'Active' : 'Inactive'}
                    </span>
                    {' · '}
                    Created {new Date(s.createdAt).toLocaleDateString()}
                  </div>
                  <div style={{ fontSize: fontSize.xs, color: colors.textMuted, marginTop: spacing.xs, fontFamily: fontFamily.mono }}>
                    ID: {s.ingestionSourceId}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: spacing.sm }}>
                  <button
                    onClick={() => handleDelete(s.ingestionSourceId)}
                    style={{ ...btnStyle, background: 'transparent', color: colors.error, border: `1px solid ${colors.error}` }}
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <div style={{ ...cardStyle, marginTop: spacing.xl, background: colors.background }}>
        <h3 style={{ margin: `0 0 ${spacing.sm}`, fontSize: fontSize.base }}>Using the API</h3>
        <pre style={{ margin: 0, fontSize: fontSize.xs, color: colors.textMuted, whiteSpace: 'pre-wrap' }}>
{`POST /api/v1/ingestion/sources/{sourceId}/fetch-and-render
Authorization: ApiKey dm_...

{
  "templateVersionId": "<uuid>",
  "query": "SELECT * FROM orders WHERE id = @RecordId",
  "recordId": "ORD-1234",
  "outputFormat": "pdf",
  "transform": {
    "rename": { "order_total": "total" },
    "remove": ["internal_notes"]
  }
}`}
        </pre>
      </div>
    </div>
      {dialogs.element}
    </>
  )
}
